import { 
  collection, 
  doc as firebaseDoc, 
  getDocs, 
  getDoc,
  setDoc, 
  updateDoc, 
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { 
  MembersPointDocument, 
  StudentPointData, 
  PointRecord, 
  PointGrantData 
} from '../types/pointManagement';

export interface UndoPointInfo {
  studentIds: string[];
  studentNames: { [key: string]: string };
  pointData?: PointGrantData; // 기존 방식용 (단일 포인트 데이터)
  pointDataList?: { [studentId: string]: PointGrantData }; // 개별 포인트 데이터용
  timestamp: number;
}

// ===== V2 듀얼 읽기: points (배열 또는 객체) → 정렬된 배열 =====
function pointsRawToArray(raw: any): PointRecord[] {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((p: any) => ({
      ...p,
      id: p.id ?? '',
      recordedDate: p.recordedDate?.toDate?.() ?? (p.recordedDate instanceof Date ? p.recordedDate : new Date((p.recordedDate?.seconds ?? 0) * 1000))
    }));
  }
  if (typeof raw === 'object') {
    const arr = Object.values(raw).map((p: any) => ({
      ...p,
      id: p.id ?? '',
      recordedDate: p.recordedDate?.toDate?.() ?? (p.recordedDate instanceof Date ? p.recordedDate : new Date((p.recordedDate?.seconds ?? 0) * 1000))
    }));
    arr.sort((a: any, b: any) => (a.recordedDate?.getTime?.() ?? 0) - (b.recordedDate?.getTime?.() ?? 0));
    return arr;
  }
  return [];
}

function studentsRawToArray(raw: any): StudentPointData[] {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((s: any) => ({
      studentId: s.studentId ?? s.id,
      studentName: s.studentName ?? '',
      studentNumber: s.studentNumber ?? '',
      points: pointsRawToArray(s.points)
    }));
  }
  if (typeof raw === 'object') {
    return Object.entries(raw).map(([studentId, s]: [string, any]) => ({
      studentId,
      studentName: s?.studentName ?? '',
      studentNumber: s?.studentNumber ?? '',
      points: pointsRawToArray(s?.points)
    }));
  }
  return [];
}

export class PointManagementService {
  private userId: string;
  private classId: string;
  private memberGroup: string;

  // 포인트 문서 캐시 (메모리)
  private pointDocumentsCache: { data: MembersPointDocument[], timestamp: number } | null = null;
  private readonly POINT_CACHE_TTL = 5 * 60 * 1000; // 5분

  // 캐시 무효화 (포인트 데이터 변경 시 호출)
  invalidatePointDocumentsCache() {
    this.pointDocumentsCache = null;
    console.log('🗑️ 포인트 문서 캐시 무효화 완료');
  }
  private lastGrantedPoints: UndoPointInfo | null = null;
  private readonly maxDocumentSize = 900000; // 900KB (1MB 제한의 90%)

  constructor(userId: string, classId: string, memberGroup?: string) {
    this.userId = userId;
    this.classId = classId;

    // 그룹 파라미터가 없으면(localStorage 기반) 현재 선택된 그룹 사용
    let resolvedGroup = memberGroup;
    if (typeof resolvedGroup === 'undefined') {
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          const key = `selected_member_group_${userId}_${classId}`;
          const saved = window.localStorage.getItem(key);
          if (saved) {
            resolvedGroup = saved;
          }
        }
      } catch {
        // ignore
      }
    }
    this.memberGroup = (resolvedGroup || 'A').toString().trim().toUpperCase();
  }

  /**
   * 그룹별 포인트 컬렉션 경로 반환
   * - A 그룹: ClassMembersPoint
   * - B 그룹: ClassMembersPoint_B
   * - C 그룹: ClassMembersPoint_C
   */
  private getCollectionPath(): string {
    const basePath = `users/${this.userId}/classes/${this.classId}`;
    if (this.memberGroup && this.memberGroup !== 'A') {
      return `${basePath}/ClassMembersPoint_${this.memberGroup}`;
    }
    return `${basePath}/ClassMembersPoint`;
  }

  private getMembersPointDocId(partitionNumber: number): string {
    if (this.memberGroup === 'A') {
      return `MembersPoint-${partitionNumber}`;
    }
    return `MembersPoint${this.memberGroup}-${partitionNumber}`;
  }

  private parseMembersPointPartitionNumber(docId: string): number | null {
    const regex =
      this.memberGroup === 'A'
        ? /^MembersPoint-(\d+)$/
        : new RegExp(`^MembersPoint${this.memberGroup}-(\\d+)$`);
    const match = docId.match(regex);
    if (!match) return null;
    return parseInt(match[1], 10);
  }

  private isCurrentGroupMembersPointDocId(docId: string): boolean {
    return this.parseMembersPointPartitionNumber(docId) !== null;
  }

  /**
   * 문서 크기 계산
   */
  private calculateDocumentSize(doc: any): number {
    return JSON.stringify(doc).length;
  }

  /**
   * 모든 MembersPoint 문서 조회 (PointTypes 제외)
   */
  async getAllMembersPointDocuments(): Promise<MembersPointDocument[]> {
    try {
      // 캐시 확인
      const now = Date.now();
      if (this.pointDocumentsCache && now - this.pointDocumentsCache.timestamp < this.POINT_CACHE_TTL) {
        console.log('💾 포인트 문서 캐시 사용 (최신):', { count: this.pointDocumentsCache.data.length, age: Math.round((now - this.pointDocumentsCache.timestamp) / 1000), unit: '초' });
        return this.pointDocumentsCache.data;
      }

      console.log('🔄 포인트 문서 캐시 없음 또는 오래됨, DB에서 로드');
      const collectionRef = collection(db, this.getCollectionPath());
      const snapshot = await getDocs(collectionRef);

      const documents: MembersPointDocument[] = [];
      snapshot.forEach(doc => {
        console.log(`🔍 발견된 문서: ${doc.id}`);
        
        // PointTypes 문서는 제외
        if (doc.id === 'PointTypes') {
          console.log(`⏭️ 제외된 문서: ${doc.id}`);
          return;
        }

        // 다른 멤버 그룹 문서 제외
        if (!this.isCurrentGroupMembersPointDocId(doc.id)) {
          return;
        }
        
        const data = doc.data();
        const partitionNumber = this.parseMembersPointPartitionNumber(doc.id) || 0;
        const students = studentsRawToArray(data.students);
        documents.push({
          id: doc.id,
          classId: data.classId || this.classId,
          documentNumber: data.documentNumber || partitionNumber,
          students,
          totalStudents: data.totalStudents ?? students.length,
          lastUpdated: data.lastUpdated?.toDate() || new Date()
        });
      });

      // 문서 번호 순으로 정렬
      const sortedDocuments = documents.sort((a, b) => a.documentNumber - b.documentNumber);

      // 캐시 저장
      this.pointDocumentsCache = {
        data: sortedDocuments,
        timestamp: now
      };
      console.log('💾 포인트 문서 캐시 저장 완료:', { count: sortedDocuments.length });

      return sortedDocuments;
    } catch (error) {
      console.error('❌ MembersPoint 문서 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 새 MembersPoint 문서 생성
   */
  async createNewMembersPointDocument(): Promise<string> {
    try {
      const documents = await this.getAllMembersPointDocuments();
      const nextNumber = documents.length > 0
        ? Math.max(...documents.map(d => d.documentNumber)) + 1
        : 1;
      const docId = this.getMembersPointDocId(nextNumber);

      console.log(`📝 새 포인트 문서 생성: ${docId} (그룹: ${this.memberGroup})`);

      // V2: 빈 객체로 시작 (멤버는 포인트 지급 시점에 동적으로 추가)
      const docRef = firebaseDoc(db, this.getCollectionPath(), docId);
      await setDoc(docRef, {
        id: docId,
        classId: this.classId,
        documentNumber: nextNumber,
        students: {},
        totalStudents: 0,
        totalSize: 0,
        lastUpdated: serverTimestamp()
      });

      console.log(`✅ 새 MembersPoint 문서 생성: ${docId} (번호: ${nextNumber})`);

      // 캐시 무효화 (새 문서가 생성되었으므로)
      this.invalidatePointDocumentsCache();

      return docId;
    } catch (error) {
      console.error('❌ MembersPoint 문서 생성 실패:', error);
      throw error;
    }
  }

  /**
   * 기존 문서의 멤버 배열 초기화
   */
  private async initializeStudentsInDocument(docId: string): Promise<void> {
    try {
      console.log(`📝 문서 ${docId}의 멤버 배열 초기화 시작`);
      
      // 클래스 멤버들 조회
      const classMembers = await this.loadClassMembers();
      console.log(`📝 조회된 클래스 멤버 수: ${classMembers.length}`);
      
      // 멤버 데이터 초기화 (빈 포인트 배열로 시작)
      const initialStudents: StudentPointData[] = classMembers.map(member => ({
        studentId: member.studentId,
        studentName: member.studentName,
        studentNumber: member.studentNumber,
        points: [],
      }));
      
      console.log(`📝 초기화된 멤버 데이터:`, initialStudents.length, '명');
      
      // 문서 업데이트
      const docRef = firebaseDoc(db, this.getCollectionPath(), docId);
      await updateDoc(docRef, {
        students: initialStudents,
        totalStudents: initialStudents.length,
        lastUpdated: serverTimestamp()
      });
      
      console.log(`✅ 문서 ${docId}의 멤버 배열 초기화 완료`);
    } catch (error) {
      console.error(`❌ 문서 ${docId}의 멤버 배열 초기화 실패:`, error);
      throw error;
    }
  }

  /**
   * 클래스 멤버들 조회
   */
  private async loadClassMembers(): Promise<any[]> {
    try {
      console.log(`🔍 클래스 멤버 조회 시작: ${this.classId}`);
      
      // ClassMembersPartitionService를 사용하여 클래스 멤버 조회
      const { ClassMembersPartitionService } = await import('./classMembersPartitionService');
      const partitionService = new ClassMembersPartitionService(this.userId, this.classId, this.memberGroup);
      const members = await partitionService.loadAllMembers();
      
      console.log(`🔍 ClassMembersPartitionService에서 조회된 멤버 수: ${members.length}`);
      
      const formattedMembers = members.map((member: any) => {
        console.log(`🔍 멤버 데이터:`, { id: member.id, name: member.name, number: member.number });
        return {
          studentId: member.id,
          studentName: member.name || '',
          studentNumber: member.number || ''
        };
      });
      
      console.log(`🔍 클래스 멤버 조회 완료: ${formattedMembers.length}명`);
      return formattedMembers;
    } catch (error) {
      console.error('❌ 클래스 멤버 조회 실패:', error);
      return [];
    }
  }

  /**
   * 안전한 타임스탬프 추출 헬퍼 함수
   */
  private getTimestamp(recordedDate: any): number {
    if (!recordedDate) return Date.now();
    
    // Date 객체인 경우
    if (recordedDate instanceof Date) {
      return recordedDate.getTime();
    }
    
    // Firestore Timestamp인 경우
    if (recordedDate.toDate && typeof recordedDate.toDate === 'function') {
      return recordedDate.toDate().getTime();
    }
    
    // 문자열인 경우
    if (typeof recordedDate === 'string') {
      const date = new Date(recordedDate);
      return isNaN(date.getTime()) ? Date.now() : date.getTime();
    }
    
    // 숫자인 경우 (이미 타임스탬프)
    if (typeof recordedDate === 'number') {
      return recordedDate;
    }
    
    return Date.now();
  }

  /**
   * 포인트 내용 기반 해시 생성 (고유 ID용)
   */
  private generatePointHash(point: any, studentId: string): string {
    // undefined/null 값을 일관되게 처리
    const content = {
      studentId: studentId || '',
      pointType: point.pointType || '일반',
      pointValue: point.pointValue || 0,
      pointMemo: point.pointMemo || point.memo || '', // memo와 pointMemo 모두 확인
      pointCategory: point.pointCategory || 'positive',
      recordedDate: this.getTimestamp(point.recordedDate)
    };
    
    // 간단한 해시 생성 (JSON 문자열의 문자 코드 합계)
    const contentString = JSON.stringify(content);
    let hash = 0;
    for (let i = 0; i < contentString.length; i++) {
      const char = contentString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // 32bit integer로 변환
    }
    
    return Math.abs(hash).toString(36); // 36진수로 변환하여 짧게 만듦
  }

  /**
   * 포인트 객체를 Firestore Timestamp로 정규화
   */
  private normalizePointForFirestore(point: any): any {
    return {
      ...point,
      recordedDate: this.normalizeDateToTimestamp(point.recordedDate)
    };
  }

  /** V2: StudentPointData[] → Firestore 저장용 객체 { [studentId]: { studentName, studentNumber, points: { [pointId]: point } } } */
  private studentsArrayToObjectForFirestore(students: StudentPointData[]): Record<string, any> {
    const obj: Record<string, any> = {};
    for (const s of students) {
      const pointsObj: Record<string, any> = {};
      for (const p of s.points) {
        const id = p.id || `point_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        pointsObj[id] = this.normalizePointForFirestore({ ...p, id });
      }
      obj[s.studentId] = {
        studentName: s.studentName,
        studentNumber: s.studentNumber || '0',
        points: pointsObj
      };
    }
    return obj;
  }

  /**
   * 날짜를 Firestore Timestamp로 정규화
   */
  private normalizeDateToTimestamp(date: any): Timestamp {
    if (!date) return Timestamp.now();
    
    // 이미 Timestamp인 경우
    if (date instanceof Timestamp) {
      return date;
    }
    
    // Date 객체인 경우
    if (date instanceof Date) {
      return Timestamp.fromDate(date);
    }
    
    // Firestore Timestamp 객체인 경우 (toDate 메서드가 있는 경우)
    if (date.toDate && typeof date.toDate === 'function') {
      return date;
    }
    
    // 문자열인 경우
    if (typeof date === 'string') {
      const parsedDate = new Date(date);
      return isNaN(parsedDate.getTime()) ? Timestamp.now() : Timestamp.fromDate(parsedDate);
    }
    
    // 숫자인 경우 (밀리초 타임스탬프)
    if (typeof date === 'number') {
      return Timestamp.fromMillis(date);
    }
    
    return Timestamp.now();
  }

  /**
   * 특정 멤버의 포인트 기록 조회
   */
  async getStudentPointHistory(studentId: string): Promise<PointRecord[]> {
    try {
      const documents = await this.getAllMembersPointDocuments();
      
      for (const doc of documents) {
        const student = doc.students.find(s => s.studentId === studentId);
        if (student) {
          // 각 포인트 기록에 고유 ID 추가 (내용 기반 해시)
          return student.points.map((point, index) => ({
            ...point,
            id: `${doc.id}_${studentId}_${this.generatePointHash(point, studentId)}_${index}`
          }));
        }
      }

      return [];
    } catch (error) {
      console.error('❌ 멤버 포인트 기록 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 마지막 지급 정보 조회
   */
  getLastGrantedPoints(): UndoPointInfo | null {
    return this.lastGrantedPoints;
  }


  /**
   * 선택된 멤버들에게 포인트 지급 (개별 포인트 데이터)
   */
  async grantPointsToStudentsWithIndividualData(
    studentIds: string[],
    pointDataList: { [studentId: string]: PointGrantData },
    studentNames: { [key: string]: string },
    studentNumbers?: { [key: string]: string }
  ): Promise<void> {
    try {
      console.log('🎯 개별 포인트 지급 시작:', { studentIds, pointDataList });

      // 마지막 지급 정보 저장 (되돌리기용)
      this.lastGrantedPoints = {
        studentIds: [...studentIds],
        studentNames: { ...studentNames },
        pointDataList: { ...pointDataList },
        timestamp: Date.now()
      };

      // 현재 문서들 조회
      let documents = await this.getAllMembersPointDocuments();

      // 문서가 없으면 첫 번째 문서 생성
      if (documents.length === 0) {
        console.log('📝 MembersPoint 문서가 없음. 새 문서 생성 중...');
        await this.createNewMembersPointDocument();
        documents = await this.getAllMembersPointDocuments();
      }

      // 업데이트할 문서들을 추적
      const docsToUpdate: Set<string> = new Set();

      // 각 멤버에 대해 포인트 추가
      for (const studentId of studentIds) {
        const pointData = pointDataList[studentId];
        if (!pointData) continue;

        // 포인트 기록 생성
        const newPointRecord: PointRecord = {
          id: `temp_${Date.now()}_${studentId}`,
          pointType: pointData.pointType || '일반',
          pointValue: pointData.pointValue || 0,
          pointMemo: pointData.pointMemo || '',
          recordedDate: pointData.recordedDate || new Date(),
          pointCategory: pointData.pointCategory || 'positive',
          ...(pointData.timeTagId && { timeTagId: pointData.timeTagId }),
          ...(pointData.timeTag && { timeTag: pointData.timeTag })
        };

        // Firestore 저장용 정규화된 포인트 기록
        const normalizedPointRecord = this.normalizePointForFirestore(newPointRecord);

        let studentFound = false;

        // 모든 문서에서 멤버 찾기
        for (const doc of documents) {
          const studentIndex = doc.students.findIndex(s => s.studentId === studentId);
          if (studentIndex !== -1) {
            studentFound = true;

            // 멤버의 포인트 배열에 새 기록 추가
            if (!doc.students[studentIndex].points) {
              doc.students[studentIndex].points = [];
            }
            doc.students[studentIndex].points.push(normalizedPointRecord);

            docsToUpdate.add(doc.id);
            break;
          }
        }

        if (!studentFound) {
          console.warn(`멤버 ${studentId}를 찾을 수 없습니다. 새로 추가합니다.`);

          // 멤버가 없으면 마지막 문서에 새로 추가
          const lastDoc = documents[documents.length - 1];

          const newStudent: StudentPointData = {
            studentId,
            studentName: studentNames[studentId] || `멤버 ${studentId.slice(-4)}`,
            studentNumber: studentNumbers?.[studentId] || '0',
            points: [normalizedPointRecord]
          };

          lastDoc.students.push(newStudent);
          docsToUpdate.add(lastDoc.id);
        }
      }

      // 변경된 문서들을 저장
      for (const docId of Array.from(docsToUpdate)) {
        const doc = documents.find(d => d.id === docId);
        if (doc) {
          const docRef = firebaseDoc(db, this.getCollectionPath(), doc.id);
          const studentsPayload = this.studentsArrayToObjectForFirestore(doc.students);
          await updateDoc(docRef, {
            students: studentsPayload,
            totalSize: this.calculateDocumentSize(doc),
            lastUpdated: serverTimestamp()
          });
        }
      }

      console.log('✅ 개별 포인트 지급 완료');

      // 데이터 변경 후 캐시 무효화
      this.invalidatePointDocumentsCache();
    } catch (error) {
      console.error('❌ 개별 포인트 지급 실패:', error);
      throw error;
    }
  }

  /**
   * 선택된 멤버들에게 포인트 지급 (기존 방식 - 모든 멤버 동일 포인트)
   */
  async grantPointsToStudents(
    studentIds: string[],
    pointData: PointGrantData,
    studentNames: { [key: string]: string },
    studentNumbers?: { [key: string]: string }
  ): Promise<void> {
    try {
      console.log('🎯 포인트 지급 시작:', { studentIds, pointData });

      // 마지막 지급 정보 저장 (되돌리기용)
      this.lastGrantedPoints = {
        studentIds: [...studentIds],
        studentNames: { ...studentNames },
        pointData: { ...pointData },
        timestamp: Date.now()
      };

      // 현재 문서들 조회
      let documents = await this.getAllMembersPointDocuments();

      // 문서가 없으면 첫 번째 문서 생성
      if (documents.length === 0) {
        console.log('📝 MembersPoint 문서가 없음. 새 문서 생성 중...');
        await this.createNewMembersPointDocument();
        documents = await this.getAllMembersPointDocuments();
        console.log('📝 포인트 문서 생성 후 documents:', documents.length, '개');
        if (documents.length > 0) {
          console.log('📝 첫 번째 문서 ID:', documents[0].id);
          console.log('📝 첫 번째 문서 students 수:', documents[0].students?.length || 'undefined');
        }
      }

      // 포인트 기록 생성 (undefined 값 방지 및 Timestamp 정규화)
      const newPointRecord: PointRecord = {
        id: `temp_${Date.now()}`, // 임시 ID, 나중에 실제 저장 시 업데이트됨
        pointType: pointData.pointType || '일반',
        pointValue: pointData.pointValue || 0,
        pointMemo: pointData.pointMemo || '',
        recordedDate: pointData.recordedDate || new Date(),
        pointCategory: pointData.pointCategory || 'positive',
        ...(pointData.timeTagId && { timeTagId: pointData.timeTagId }),
        ...(pointData.timeTag && { timeTag: pointData.timeTag })
      };

      // Firestore 저장용 정규화된 포인트 기록
      const normalizedPointRecord = this.normalizePointForFirestore(newPointRecord);

      // 업데이트할 문서들을 추적
      const docsToUpdate: Set<string> = new Set();

      // 각 멤버에 대해 포인트 추가
      for (const studentId of studentIds) {
        let studentFound = false;

        // 모든 문서에서 멤버 찾기
        for (const doc of documents) {
          if (!Array.isArray(doc.students)) {
            console.error(`❌ 문서 ${doc.id}의 students가 배열이 아닙니다:`, doc.students);
            continue;
          }

          const studentIndex = doc.students.findIndex(s => s.studentId === studentId);
          if (studentIndex !== -1) {
            // 멤버가 있으면 포인트 추가 (정규화된 포인트 사용)
            doc.students[studentIndex].points.push(normalizedPointRecord);
            docsToUpdate.add(doc.id);
            studentFound = true;
            break;
          }
        }

        // 멤버가 없으면 마지막 문서에 새로 추가
        if (!studentFound) {
          const lastDoc = documents[documents.length - 1];
          
          // 마지막 문서 크기 확인 (1MB 제한)
          const currentSize = this.calculateDocumentSize(lastDoc);
          lastDoc.totalSize = currentSize;

          // 크기가 초과하면 새 문서 생성
          if (currentSize > this.maxDocumentSize) {
            console.log(`📄 문서 크기 초과 (${currentSize} bytes / ${this.maxDocumentSize} bytes) - 새 문서 생성`);
            await this.createNewMembersPointDocument();
            documents = await this.getAllMembersPointDocuments();
          }

          const targetDoc = documents[documents.length - 1];
          const newStudent: StudentPointData = {
            studentId,
            studentName: studentNames[studentId] || `멤버 ${studentId.slice(-4)}`,
            studentNumber: studentNumbers?.[studentId] || '0',
            points: [normalizedPointRecord]
          };
          targetDoc.students.push(newStudent);
          docsToUpdate.add(targetDoc.id);
        }
      }

      // 변경된 모든 문서 업데이트
      const docIdsArray = Array.from(docsToUpdate);
      for (const docId of docIdsArray) {
        const doc = documents.find(d => d.id === docId);
        if (doc) {
          // undefined 값 제거 및 데이터 정리
          const cleanedStudents = doc.students.map(student => ({
            ...student,
            studentNumber: student.studentNumber || '0',
            points: student.points.map(point => ({
              ...point,
              pointType: point.pointType || '일반',
              pointValue: point.pointValue || 0,
              pointMemo: point.pointMemo || '',
              pointCategory: point.pointCategory || 'positive',
              recordedDate: point.recordedDate || new Date()
            }))
          }));

          // 문서 크기 계산 및 업데이트
          const updatedSize = this.calculateDocumentSize(doc);
          doc.totalSize = updatedSize;

          console.log(`🔍 업데이트할 문서 ID: ${docId}, 경로: ${this.getCollectionPath()}/${docId}, 크기: ${updatedSize} bytes`);
          const docRef = firebaseDoc(db, this.getCollectionPath(), docId);
          const studentsPayload = this.studentsArrayToObjectForFirestore(cleanedStudents);
          await updateDoc(docRef, {
            students: studentsPayload,
            totalStudents: cleanedStudents.length,
            totalSize: updatedSize,
            lastUpdated: serverTimestamp()
          });
          console.log(`✅ 문서 업데이트 완료: ${docId} (${cleanedStudents.length}명, V2)`);
        }
      }

      console.log(`✅ 포인트 지급 완료 (업데이트된 문서: ${docIdsArray.length}개)`);

      // 데이터 변경 후 캐시 무효화
      this.invalidatePointDocumentsCache();
    } catch (error) {
      console.error('❌ 포인트 지급 실패:', error);
      throw error;
    }
  }

  /**
   * 모든 멤버의 포인트 통계 조회
   */
  async getAllStudentsPointSummary(): Promise<{ [studentId: string]: { totalPoints: number; pointCount: number } }> {
    try {
      const documents = await this.getAllMembersPointDocuments();
      const summary: { [studentId: string]: { totalPoints: number; pointCount: number } } = {};

      for (const doc of documents) {
        for (const student of doc.students) {
          if (!summary[student.studentId]) {
            summary[student.studentId] = { totalPoints: 0, pointCount: 0 };
          }

          const studentTotal = student.points.reduce((sum, point) => sum + point.pointValue, 0);
          summary[student.studentId].totalPoints += studentTotal;
          summary[student.studentId].pointCount += student.points.length;
        }
      }

      return summary;
    } catch (error) {
      console.error('❌ 멤버 포인트 통계 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 최근 지급한 포인트 되돌리기
   */
  async undoLastGrantedPoints(): Promise<{ success: boolean; undoInfo: UndoPointInfo | null; message: string }> {
    try {
      if (!this.lastGrantedPoints) {
        return {
          success: false,
          undoInfo: null,
          message: '되돌릴 최근 포인트 지급 내역이 없습니다.'
        };
      }

      const { studentIds, pointData, pointDataList, timestamp } = this.lastGrantedPoints;
      console.log('🔄 포인트 되돌리기 시작:', { studentIds, pointData, pointDataList, timestamp });

      // 현재 문서들 조회
      const documents = await this.getAllMembersPointDocuments();
      console.log('📄 조회된 문서 수:', documents.length);

      let undoCount = 0;
      const undoDetails: string[] = [];

      // 각 문서별로 처리하여 효율성 향상
      for (const doc of documents) {
        console.log(`🔍 문서 처리 중: ${doc.id}, 멤버 수: ${doc.students?.length || 0}`);
        if (!Array.isArray(doc.students)) continue;

        let docUpdated = false;
        const updatedStudents = [...doc.students];
        
        // 해당 문서에서 되돌릴 멤버들 찾기
        for (const studentId of studentIds) {
          console.log(`🔍 멤버 ${studentId} 검색 중...`);
          const studentIndex = updatedStudents.findIndex(s => s.studentId === studentId);
          if (studentIndex !== -1) {
            console.log(`✅ 멤버 ${studentId} 발견 (인덱스: ${studentIndex})`);
            const student = updatedStudents[studentIndex];
            
            // 해당 학생의 포인트 데이터 가져오기 (pointDataList 우선, 없으면 pointData 사용)
            const studentPointData = pointDataList?.[studentId] || pointData;
            
            if (!studentPointData) {
              console.log(`⚠️ ${studentId}의 포인트 데이터를 찾을 수 없음`);
              continue;
            }
            
            // 최근 포인트 찾기 (timestamp와 pointData가 일치하는 것)
            const pointIndex = student.points.findIndex(p => {
              const pointTimestamp = this.getTimestamp(p.recordedDate);
              // timestamp 차이가 5초 이내이고 pointValue와 pointType이 일치하는 경우
              const timestampMatch = Math.abs(pointTimestamp - timestamp) < 5000;
              const valueMatch = p.pointValue === (studentPointData?.pointValue || 0);
              const typeMatch = p.pointType === (studentPointData?.pointType || '');
              
              console.log(`🔍 포인트 매칭 체크:`, {
                studentId,
                pointTimestamp,
                targetTimestamp: timestamp,
                timestampDiff: Math.abs(pointTimestamp - timestamp),
                timestampMatch,
                valueMatch,
                typeMatch,
                pointValue: p.pointValue,
                targetValue: studentPointData?.pointValue || 0,
                pointType: p.pointType,
                targetType: studentPointData?.pointType || ''
              });
              
              return timestampMatch && valueMatch && typeMatch;
            });

            if (pointIndex !== -1) {
              // 포인트 삭제
              updatedStudents[studentIndex].points.splice(pointIndex, 1);
              docUpdated = true;
              undoCount++;
              undoDetails.push(`${this.lastGrantedPoints.studentNames[studentId] || studentId}: ${studentPointData?.pointValue || 0}점`);
              console.log(`✅ ${studentId} 포인트 되돌리기 완료 (인덱스: ${pointIndex})`);
            } else {
              console.log(`❌ ${studentId}의 매칭되는 포인트를 찾지 못함`);
            }
          } else {
            console.log(`❌ 멤버 ${studentId}를 문서에서 찾지 못함`);
          }
        }
        
        // 문서에 변경사항이 있으면 업데이트
        if (docUpdated) {
          console.log(`📝 문서 ${doc.id} 업데이트 중...`);
          const studentsPayload = this.studentsArrayToObjectForFirestore(updatedStudents);
          const updatedDoc = { ...doc, students: updatedStudents };
          const updatedSize = this.calculateDocumentSize(updatedDoc);
          const docRef = firebaseDoc(db, this.getCollectionPath(), doc.id);
          await updateDoc(docRef, {
            students: studentsPayload,
            totalSize: updatedSize,
            lastUpdated: serverTimestamp()
          });
          console.log(`✅ 문서 ${doc.id} 업데이트 완료 (크기: ${updatedSize} bytes, V2)`);
        } else {
          console.log(`⏭️ 문서 ${doc.id}에 변경사항 없음`);
        }
      }

      const undoInfo = { ...this.lastGrantedPoints };
      this.lastGrantedPoints = null; // 되돌리기 완료 후 초기화

      return {
        success: true,
        undoInfo,
        message: `${undoCount}명의 포인트 지급을 되돌렸습니다.`
      };

    } catch (error) {
      console.error('❌ 포인트 되돌리기 실패:', error);
      throw error;
    }
  }

  /**
   * 멤버의 여러 포인트 기록을 배치로 삭제 (필터링 방식)
   */
  async deleteStudentPointsBatch(studentId: string, pointIds: string[]): Promise<void> {
    try {
      console.log(`🗑️ 배치 삭제 시작 - studentId: ${studentId}, 삭제할 포인트 수: ${pointIds.length}`);
      
      const documents = await this.getAllMembersPointDocuments();
      let hasChanges = false;
      
      for (const doc of documents) {
        const studentIndex = doc.students.findIndex(s => s.studentId === studentId);
        if (studentIndex === -1) continue;
        
        const student = doc.students[studentIndex];
        const originalPointsCount = student.points.length;
        
        // 삭제할 포인트들을 필터링으로 제거
        const updatedPoints = student.points.filter(point => {
          // 안전한 날짜 변환
          let recordedDate: Date;
          if (point.recordedDate instanceof Date) {
            recordedDate = point.recordedDate;
          } else if (point.recordedDate && typeof point.recordedDate === 'object' && 'toDate' in point.recordedDate) {
            recordedDate = (point.recordedDate as any).toDate();
          } else {
            recordedDate = new Date(point.recordedDate);
          }
          
          const pointId = `${recordedDate.getTime()}_${point.pointValue}_${point.pointMemo}_${point.pointCategory}`;
          const shouldKeep = !pointIds.includes(pointId);
          
          if (!shouldKeep) {
            console.log(`🗑️ 포인트 삭제 예정: ${point.pointMemo} (${point.pointValue}점)`);
          }
          
          return shouldKeep;
        });
        
        if (updatedPoints.length !== originalPointsCount) {
          student.points = updatedPoints;
          hasChanges = true;
          console.log(`📊 멤버 ${studentId} 포인트 변화: ${originalPointsCount} → ${updatedPoints.length}`);
        }
      }
      
      if (hasChanges) {
        for (const doc of documents) {
          const docRef = firebaseDoc(db, this.getCollectionPath(), doc.id);
          const studentsPayload = this.studentsArrayToObjectForFirestore(doc.students);
          await updateDoc(docRef, {
            students: studentsPayload,
            totalSize: this.calculateDocumentSize(doc),
            lastUpdated: serverTimestamp()
          });
        }
        console.log(`✅ 배치 삭제 완료: ${pointIds.length}개 포인트 삭제됨`);
        
        // 레벨 다운 감지 및 currentLevel 업데이트 (보상은 포인트 조회 페이지에서만 지급)
        try {
          const { PointTypeManagementService } = await import('./pointTypeManagementService');
          const { ClassMembersPartitionService } = await import('./classMembersPartitionService');
          const { PointsQueryService } = await import('./pointsQueryService');
          
          // 삭제된 포인트의 studentId 추출 (각 문서에서 확인)
          const affectedStudentIds = new Set<string>();
          for (const doc of documents) {
            for (const student of doc.students) {
              const originalPoints = student.points || [];
              const updatedPoints = originalPoints.filter((point: any) => {
                const pointId = `${point.recordedDate?.toDate?.()?.getTime() || new Date(point.recordedDate).getTime()}_${point.pointValue}_${point.pointMemo}_${point.pointCategory}`;
                return !pointIds.includes(pointId);
              });
              
              if (updatedPoints.length !== originalPoints.length) {
                affectedStudentIds.add(student.studentId);
              }
            }
          }
          
          // 각 학생에 대해 레벨 다운 감지
          for (const studentId of Array.from(affectedStudentIds)) {
            try {
              // 삭제 전/후 총점 계산을 위해 현재 상태 확인
              const currentDoc = documents.find(d => d.students.some(s => s.studentId === studentId));
              if (!currentDoc) continue;
              
              const student = currentDoc.students.find(s => s.studentId === studentId);
              if (!student) continue;
              
              // 삭제 후 rewardPoints (현재 상태)
              const newRewardPoints = (student.points || []).reduce((sum: number, p: any) => sum + (p?.pointValue || 0), 0);
              
              // 삭제된 포인트의 합계 계산
              const deletedPoints = student.points?.filter((point: any) => {
                const pointId = `${point.recordedDate?.toDate?.()?.getTime() || new Date(point.recordedDate).getTime()}_${point.pointValue}_${point.pointMemo}_${point.pointCategory}`;
                return pointIds.includes(pointId);
              }) || [];
              const deletedPointsSum = deletedPoints.reduce((sum: number, p: any) => sum + (p?.pointValue || 0), 0);
              
              // 삭제 전 rewardPoints
              const oldRewardPoints = newRewardPoints + deletedPointsSum;

              // 과제 포인트는 과제 통합 문서에서 직접 집계 (membersPoints 하위 컬렉션에 의존하지 않음)
              const pointsQueryService = new PointsQueryService(this.userId, this.classId, this.memberGroup);
              const assignmentPointsTotal = await pointsQueryService.getStudentAssignmentPointsTotal(studentId);

              // 총점 계산 (rewardPoints + 과제포인트)
              const oldTotalPoints = oldRewardPoints + assignmentPointsTotal;
              const newTotalPoints = newRewardPoints + assignmentPointsTotal;
              
              const pointTypeService = new PointTypeManagementService(this.userId, this.classId);
              const pointTypeData = await pointTypeService.getPointTypeData();
              const levelUpPoints = pointTypeData.levelUpPoints || 50;
              
              // 레벨 계산
              const calcLevel = (totalPoints: number) => Math.floor((totalPoints || 0) / levelUpPoints);
              const oldLevel = calcLevel(oldTotalPoints);
              const newLevel = calcLevel(newTotalPoints);
              
              // 레벨 변화가 있으면 currentLevel만 업데이트 (보상은 포인트 조회 페이지에서 처리)
              if (oldLevel !== newLevel) {
                const partitionService = new ClassMembersPartitionService(this.userId, this.classId, this.memberGroup);
                await partitionService.updateStudentCurrentLevel(studentId, newLevel);
                console.log(`✅ 레벨 변화 감지 (보상은 포인트 조회 페이지에서 처리): ${studentId}, 레벨 ${oldLevel} → ${newLevel}`);
              }
            } catch (error) {
              console.error(`레벨 변화 감지 실패 (멤버 ${studentId}):`, error);
            }
          }
        } catch (error) {
          console.error('레벨 변화 감지 중 오류:', error);
        }
      } else {
        console.log(`⚠️ 삭제할 포인트를 찾을 수 없음`);
      }
      
    } catch (error) {
      console.error('❌ 배치 삭제 실패:', error);
      throw error;
    }
  }

  /**
   * 멤버의 특정 포인트 기록 삭제
   */
  async deleteStudentPoint(studentId: string, pointId: string): Promise<void> {
    try {
      console.log(`🔍 삭제 시도 - studentId: ${studentId}, pointId: ${pointId}`);
      
      // pointId에서 문서 정보 추출: "MembersPoint-1_studentId_hash_index"
      const parts = pointId.split('_');
      if (parts.length < 4) {
        console.error('❌ pointId 형식 오류:', { pointId, parts });
        throw new Error('잘못된 포인트 ID 형식입니다.');
      }
      
      const docId = parts[0]; // "MembersPoint-1"
      const targetHash = parts[2]; // 해시값
      const targetIndex = parseInt(parts[3]); // 인덱스
      
      console.log(`🔍 파싱된 정보 - docId: ${docId}, hash: ${targetHash}, index: ${targetIndex}`);
      
      // 해당 문서 조회
      const docRef = firebaseDoc(db, this.getCollectionPath(), docId);
      console.log(`🔍 문서 경로: ${this.getCollectionPath()}/${docId}`);
      
      const docSnapshot = await getDoc(docRef);
      
      if (!docSnapshot.exists()) {
        console.error('❌ 문서 없음:', { collectionPath: this.getCollectionPath(), docId });
        throw new Error('해당 문서를 찾을 수 없습니다.');
      }
      
      const docData = docSnapshot.data();
      console.log(`📄 문서 데이터:`, { 
        hasStudents: !!docData.students, 
        studentsCount: docData.students?.length || 0 
      });
      
      // 해당 멤버의 데이터가 있는지 확인
      if (docData.students && Array.isArray(docData.students)) {
        const studentIndex = docData.students.findIndex((s: any) => s.studentId === studentId);
        console.log(`🔍 멤버 인덱스: ${studentIndex}`);
        
        if (studentIndex === -1) {
          console.error('❌ 멤버를 찾을 수 없음:', { studentId, availableStudents: docData.students.map((s: any) => s.studentId) });
          throw new Error('해당 멤버를 찾을 수 없습니다.');
        }
        
        if (!docData.students[studentIndex].points || !Array.isArray(docData.students[studentIndex].points)) {
          console.error('❌ 멤버의 포인트 배열이 없음:', { studentId });
          throw new Error('멤버의 포인트 기록이 없습니다.');
        }
        
        const points = docData.students[studentIndex].points;
        console.log(`📊 멤버의 포인트 수: ${points.length}개`);
          
        // 포인트 ID로 직접 매칭 (가장 정확한 방법)
        let targetPointIndex = -1;
        
        // 1. 포인트 ID로 직접 매칭 시도
        console.log(`🔍 포인트 ID 매칭 시도: ${pointId}`);
        for (let i = 0; i < points.length; i++) {
          const point = points[i];
          if (point.id && point.id === pointId) {
            targetPointIndex = i;
            console.log(`🎯 포인트 ID로 직접 매칭 성공 - 인덱스: ${i}, ID: ${point.id}`);
            break;
          }
        }
        
        // 2. 포인트 ID 매칭이 실패한 경우, 인덱스 기반 매칭 시도
        if (targetPointIndex === -1) {
          console.log(`⚠️ 포인트 ID 매칭 실패. 인덱스 기반 매칭 시도: targetIndex=${targetIndex}, points.length=${points.length}`);
        }
        
        if (targetPointIndex === -1 && targetIndex < points.length) {
            const pointAtTargetIndex = points[targetIndex];
            console.log(`🔍 인덱스 ${targetIndex}의 포인트 확인:`, {
              pointType: pointAtTargetIndex.pointType,
              pointValue: pointAtTargetIndex.pointValue,
              pointMemo: pointAtTargetIndex.pointMemo,
              recordedDate: pointAtTargetIndex.recordedDate
            });
            
            // 해시 방식으로 검증 (선택적)
            const pointHash = this.generatePointHash(pointAtTargetIndex, studentId);
            console.log(`🔍 해시 비교 - 생성된 해시: ${pointHash}, 대상 해시: ${targetHash}`);
            
            if (pointHash === targetHash) {
              targetPointIndex = targetIndex;
              console.log(`🎯 해시 검증으로 매칭된 포인트 발견 - 인덱스: ${targetIndex}`);
            } else {
              // 해시가 다르더라도 인덱스가 정확하다면 삭제 허용 (데이터 변경 가능성 고려)
              console.log(`⚠️ 해시가 다르지만 인덱스 기반으로 삭제 진행 - 인덱스: ${targetIndex}`);
              targetPointIndex = targetIndex;
            }
          }
          
        if (targetPointIndex === -1) {
          console.error('❌ 포인트를 찾을 수 없음:', {
            pointId,
            studentId,
            targetIndex,
            pointsLength: points.length,
            availablePointIds: points.map((p: any, i: number) => ({ index: i, id: p.id, type: p.pointType, value: p.pointValue }))
          });
          throw new Error('해당 포인트 기록을 찾을 수 없습니다.');
        }
          
          const targetPoint = points[targetPointIndex];
          console.log(`🗑️ 삭제할 포인트 정보:`, {
            pointType: targetPoint.pointType,
            pointValue: targetPoint.pointValue,
            pointMemo: targetPoint.pointMemo,
            recordedDate: targetPoint.recordedDate,
            id: targetPoint.id
          });
          
        // 일반 과제 삭제와 완전히 동일한 방식: 전체 배열 교체
        console.log(`🔧 삭제 실행: studentIndex=${studentIndex}, targetPointIndex=${targetPointIndex}`);
        const updatedStudents = [...docData.students];
        const beforeLength = updatedStudents[studentIndex].points.length;
        updatedStudents[studentIndex].points.splice(targetPointIndex, 1);
        const afterLength = updatedStudents[studentIndex].points.length;
        console.log(`📊 포인트 배열 길이 변화: ${beforeLength} → ${afterLength}`);
        
        // 문서 크기 계산
        const updatedDoc = { ...docData, students: updatedStudents };
        const updatedSize = this.calculateDocumentSize(updatedDoc);
        console.log(`📏 문서 크기: ${updatedSize} bytes`);
        const studentsPayload = this.studentsArrayToObjectForFirestore(updatedStudents);
        console.log(`💾 Firestore 업데이트 시작...`);
        await updateDoc(docRef, {
          students: studentsPayload,
          totalSize: updatedSize,
          lastUpdated: serverTimestamp()
        });
        
        // 레벨 다운 감지 및 currentLevel 업데이트 (보상은 포인트 조회 페이지에서만 지급)
        try {
          const { PointTypeManagementService } = await import('./pointTypeManagementService');
          const { ClassMembersPartitionService } = await import('./classMembersPartitionService');
          const { PointsQueryService } = await import('./pointsQueryService');
          
          // 삭제 전 총점 계산
          const oldRewardPoints = docData.students[studentIndex].points.reduce((sum: number, p: any) => sum + (p?.pointValue || 0), 0);
          // 삭제 후 총점 계산
          const newRewardPoints = updatedStudents[studentIndex].points.reduce((sum: number, p: any) => sum + (p?.pointValue || 0), 0);
          
          // 과제 포인트는 과제 통합 문서에서 직접 집계 (membersPoints 하위 컬렉션에 의존하지 않음)
          const pointsQueryService = new PointsQueryService(this.userId, this.classId, this.memberGroup);
          const assignmentPointsTotal = await pointsQueryService.getStudentAssignmentPointsTotal(studentId);
          
          // 총점 계산 (rewardPoints + 과제포인트)
          const oldTotalPoints = oldRewardPoints + assignmentPointsTotal;
          const newTotalPoints = newRewardPoints + assignmentPointsTotal;
          
          const pointTypeService = new PointTypeManagementService(this.userId, this.classId);
          const pointTypeData = await pointTypeService.getPointTypeData();
          const levelUpPoints = pointTypeData.levelUpPoints || 50;
          
          // 레벨 계산
          const calcLevel = (totalPoints: number) => Math.floor((totalPoints || 0) / levelUpPoints);
          const oldLevel = calcLevel(oldTotalPoints);
          const newLevel = calcLevel(newTotalPoints);
          
          // 레벨 변화가 있으면 currentLevel만 업데이트 (보상은 포인트 조회 페이지에서 처리)
          if (oldLevel !== newLevel) {
            const partitionService = new ClassMembersPartitionService(this.userId, this.classId, this.memberGroup);
            await partitionService.updateStudentCurrentLevel(studentId, newLevel);
            console.log(`✅ 레벨 변화 감지 (보상은 포인트 조회 페이지에서 처리): ${studentId}, 레벨 ${oldLevel} → ${newLevel}`);
          }
        } catch (error) {
          console.error(`레벨 변화 감지 실패 (멤버 ${studentId}):`, error);
          // 레벨 감지 실패해도 포인트 삭제는 성공한 상태이므로 에러를 다시 던지지 않음
        }
        
        console.log(`✅ 포인트 기록 삭제 완료: 멤버 ${studentId}`);
        return;
      }
      
      console.error('❌ students 배열이 없거나 배열이 아님');
      throw new Error('해당 포인트 기록을 찾을 수 없습니다.');
    } catch (error) {
      console.error('❌ 포인트 기록 삭제 실패:', error);
      throw error;
    }
  }
}

export default PointManagementService;
